import React from "react";
import { Link } from "react-router-dom";
import { twMerge } from "tailwind-merge";
import { Crown } from "lucide-react";
import { weightClassesName } from "../constants";
import { getEventFighterWeightClass } from "../utils/common";

/**
 * FighterMatchCard - A reusable component to display a match between two fighters
 * 
 * @param {Object} props
 * @param {Object} props.fighterA - First fighter's event fighter data
 * @param {Object} props.fighterB - Second fighter's event fighter data
 * @param {number} props.matchNumber - Optional match number to display
 * @param {boolean} props.showMatchNumber - Whether to show the match number badge
 * @param {string} props.winnerId - ID of the winning fighter (for crown display)
 * @param {React.ReactNode} props.children - Optional children to render below the match display
 * @param {string} props.className - Additional classes for the container
 */
export default function FighterMatchCard({
    fighterA,
    fighterB,
    matchNumber,
    showMatchNumber = false,
    winnerId,
    children,
    className,
}) {
    // Calculate if fighters have same entry and weight class
    const weightClassA = getEventFighterWeightClass(fighterA);
    const weightClassB = getEventFighterWeightClass(fighterB);
    const sameEntry = fighterA?.entry === fighterB?.entry;
    const sameWeightClass = weightClassA === weightClassB;
    const sameEntrySameWeightClass = sameEntry && sameWeightClass;

    return (
        <div
            className={twMerge(
                "flex-center w-full gap-2 relative",
                className
            )}
        >
            {/* Match Number Badge */}
            {showMatchNumber && matchNumber !== undefined && (
                <p className="absolute top-0 left-0 font-bold text-sm p-2 border w-8 h-8 flex-center -translate-x-[1px] -translate-y-[1px] leading-none">
                    {matchNumber}
                </p>
            )}

            {/* Fighter Match Display */}
            <div className="flex flex-row gap-2 w-full justify-between">
                {/* Fighter A */}
                <Link
                    to={`/fighter/${fighterA?.fighterId}`}
                    className="flex flex-col items-center gap-2 p-2 w-full hover:opacity-80 transition-opacity"
                >
                    <div className="w-24 aspect-square relative">
                        <img
                            className="w-full h-full object-center object-cover aspect-square"
                            src={
                                fighterA?.fighter?.fighterPicture ||
                                "/assets/img/example_fighter_picture.png"
                            }
                            alt={fighterA?.fighter?.name || "Fighter A"}
                        />
                        {winnerId === fighterA?.fighterId && (
                            <div className="absolute -top-2 -right-2 bg-white text-black rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold p-2">
                                <Crown />
                            </div>
                        )}
                    </div>
                    <p className="font-bold text-center">
                        {fighterA?.fighter?.name || "Unknown Fighter"}
                    </p>
                </Link>

                {/* Middle Section - Entry, Weight Class, and VS */}
                <div className="flex-center gap-2 w-full">
                    {/* Fighter A Entry and Weight Class */}
                    {!sameEntrySameWeightClass && (
                        <>
                            <p className="self-start text-sm leading-none font-bold">
                                {fighterA?.entry || "Unknown Entry"}
                            </p>
                            <p className="self-start text-sm leading-none font-bold">
                                {(() => {
                                    const wc = getEventFighterWeightClass(fighterA);
                                    return wc
                                        ? `${wc}: ${weightClassesName[wc]}`
                                        : "Unknown Weight Class";
                                })()}
                            </p>
                        </>
                    )}

                    {/* VS Divider */}
                    <div className="divider m-0">vs</div>

                    {/* Fighter B Entry and Weight Class */}
                    <p
                        className={twMerge(
                            "text-sm leading-none font-bold",
                            !sameEntrySameWeightClass && "self-end"
                        )}
                    >
                        {fighterB?.entry || "Unknown Entry"}
                    </p>
                    <p
                        className={twMerge(
                            "text-sm leading-none font-bold",
                            !sameEntrySameWeightClass && "self-end"
                        )}
                    >
                        {(() => {
                            const wc = getEventFighterWeightClass(fighterB);
                            return wc
                                ? `${wc}: ${weightClassesName[wc]}`
                                : "Unknown Weight Class";
                        })()}
                    </p>
                </div>

                {/* Fighter B */}
                <Link
                    to={`/fighter/${fighterB?.fighterId}`}
                    className="flex flex-col items-center gap-2 p-2 w-full hover:opacity-80 transition-opacity"
                >
                    <div className="w-24 aspect-square relative">
                        <img
                            className="w-full h-full object-center object-cover aspect-square"
                            src={
                                fighterB?.fighter?.fighterPicture ||
                                "/assets/img/example_fighter_picture.png"
                            }
                            alt={fighterB?.fighter?.name || "Fighter B"}
                        />
                        {winnerId === fighterB?.fighterId && (
                            <div className="absolute -top-2 -right-2 bg-white text-black rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold p-2">
                                <Crown />
                            </div>
                        )}
                    </div>
                    <p className="font-bold text-center">
                        {fighterB?.fighter?.name || "Unknown Fighter"}
                    </p>
                </Link>
            </div>

            {/* Optional children content (e.g., additional buttons or info) */}
            {children}
        </div>
    );
}
